import { Component, Input } from '@angular/core';
import { PopoverController, LoadingController, ModalController, AlertController, TabHighlight, NavController, Events } from 'ionic-angular';

import { Rodeo } from '../../../../model/rodeo';
import { RodeoEvent } from '../../../../model/rodeoEvent';

import { UserSearchPopover } from '../../../search/user-search';
import { HorseSearchPopover } from '../../../search/horse-search';
import { Horse } from '../../../../model/horse';
import { HorseService } from '../../../../services/horse.service';
import { AlertMessager } from '../../../../lib/alertMessager';

import { Livestock } from '../../../../model/livestock';
import { ApiService } from '../../../../services/api';
import { Winner } from '../../../../model/winner';
import { NewWinnerPage } from './new-winner';
import { PayoutFormat } from '../../../../model/payoutFormat';
import { Account } from '../../../../model/account';
import { PrintChecksComponent } from '../../../print-checks/print-checks.component';
import { LeaderGenerator } from '../../../../lib/leaderGenerator';
import { ResultsReport } from '../reports/report-results';
import { PayoutConfig } from '../../../../model/payoutConfig';
import { WinnerService } from '../../../../services/winner.service';
import { Leader } from 'lib/types/leader';
import { PointsDomainService } from '../../../../services/points/points.domian.service';
import { OrganizationCheckRegister } from '../../../../model/organizationCheckRegister';
import { MonitoringService } from '../../../../services/AppInsights/monitoring-service';
import { PointGenerationModalComponent } from './points-generation-modal/point-generation-modal.component';
import { RunDisplayHelper } from 'lib/run-display-helper';

@Component({
    selector: 'staff-results',
    templateUrl: 'results.html'
})
export class ResultsSection {
    @Input('viewMode') viewMode: string;

    _rodeo: Rodeo = new Rodeo();
    selectedRodeoEvent: RodeoEvent;

    places: number = 1;
    totalPot: number = 0;
    totalCount: number = 0;
    membershipDiscountTotal: number = 0;
    addedMoney: number = 0;
    addedDistribution = 'Progressive';
    selectedPayoutFormat: PayoutFormat;
    selectedPayout: PayoutConfig;
    rounding: string = "1";
    payoutSelections: PayoutConfig[];
    paybackPercentage: number = 80;
    placeEvery: number = 25;

    equalDPots: boolean = false;
    displayWinners: Winner[];
    leaders: Leader[];

    projectedPot: number = 0;
    availablePlaces: any[];
    testPay: boolean;

    popoverActive: boolean = false;

    @Input()
    set rodeo(rodeo: Rodeo) {
        if (rodeo != null) {
            this._rodeo = new Rodeo();
            Object.assign(this._rodeo, rodeo);

            this.selectedRodeoEvent = null;
            this.selectedPayout = null;
            this.selectedPayoutFormat = null;

            this._rodeo.rodeoEvents.forEach((rodeoEvent) => {
                if (rodeoEvent.eventType === 'BullRiding' || rodeoEvent.eventType === 'SaddleBroncRiding' || rodeoEvent.eventType === 'RanchBroncRiding'
                    || rodeoEvent.eventType === 'BarebackRiding')
                    rodeoEvent.roughStock = true;
            });

            if (this.selectedRodeoEvent == null || !this._rodeo.rodeoEvents.some(x => x.id == this.selectedRodeoEvent.id)) {
                this.selectedRodeoEvent = this._rodeo.rodeoEvents[0];

                this.rebuildPayoutSelections();

                this.selectedPayout = this.payoutSelections[0];
            }

            if (!this.selectedPayoutFormat || !this._rodeo.organization.payoutFormats.some(x => x.payoutFormat.id == this.selectedPayoutFormat.id))
                this.selectedPayoutFormat = this._rodeo.organization.payoutFormats[0].payoutFormat;

            const entryFee = this.selectedRodeoEvent.priceTiers.length > 0 ? this.selectedRodeoEvent.priceTiers[0].unitPrice : 0;
            this.totalPot = this.selectedRodeoEvent.registrations.length * entryFee * .8;

            if (this.selectedPayout)
                this.getTotalPot(this.selectedPayout, true);
            
            this.filterWinners();
            this.updatedPayoutFormat();
        }
    }
    get rodeo(): Rodeo {
        return this._rodeo;
    }

    constructor(private popoverController: PopoverController, private modalCtrl: ModalController, public api: ApiService,
        private horseService: HorseService, private loadingControl: LoadingController, private alertController: AlertController,
        private alert: AlertMessager, private winnerService: WinnerService, private nav: NavController,
        private events: Events, private pointsDomainService: PointsDomainService,
        private monitoringService: MonitoringService
    ) {
            this.events.subscribe('WinnerAdded', (data) => this.winnersAdded(data));
            this.events.subscribe('WinnerUpdated', (data) => this.winnersUpdated(data));
            this.events.subscribe('WinnerDeleted', (data) => this.winnersDeleted(data));
            this.monitoringService.logPageView('Results');
        }

    stopTimerLoops() {
        this.events.unsubscribe('WinnerAdded', (data) => this.winnersAdded(data));
        this.events.unsubscribe('WinnerUpdated', (data) => this.winnersUpdated(data));
        this.events.unsubscribe('WinnerDeleted', (data) => this.winnersDeleted(data));
    }

    winnersUpdated(updated) {
        updated.forEach(winner => {
            let foundWinner = false;
            for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                if (this.rodeo.rodeoEvents[i].id != winner.rodeoEventScheduleId)
                    continue;

                for (let x = 0; x < this.rodeo.rodeoEvents[i].winners.length; x++) {
                    if (winner.id == this.rodeo.rodeoEvents[i].winners[x].id) {
                        Object.assign(this.rodeo.rodeoEvents[i].winners[x], winner);
                        foundWinner = true;
                        break;
                    }
                }

                if (foundWinner)
                    break;
            }
        });

        if (updated.length > 0) {
            this.filterWinners();
        }
    }

    winnersDeleted(deleted) {
        deleted.forEach(runUpdateInfo => {
            this.displayWinners = this.displayWinners.filter(x => x.id != runUpdateInfo.id);
        });

        if (deleted.length > 0) {
            this.filterWinners();
        }
    }

    winnersAdded(added) {
        if (added.length > 0) {
            this.filterWinners();
        }
    }
    
    openUserSearch(event, rodeoEvent, winnerIndex: number, teammateSearch) {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        let popover = this.popoverController.create(UserSearchPopover, {
            searchTitle: (teammateSearch ? this.getSecondLabel(rodeoEvent) : this.getFirstLabel(rodeoEvent)) + " Search"
        }, { cssClass: 'user-search' });

        popover.present({ ev: event });

        popover.onDidDismiss((data) => {
            this.popoverActive = false;

            if (data != null) {
                if (teammateSearch) {
                    this.selectedRodeoEvent.winners[winnerIndex].contestantTwoId = data.buddies[0].id;
                    this.selectedRodeoEvent.winners[winnerIndex].contestantTwo = data.buddies[0];
                    this.selectedRodeoEvent.winners[winnerIndex].horseTwo = null;
                } else {
                    this.selectedRodeoEvent.winners[winnerIndex].contestantOneId = data.buddies[0].id;
                    this.selectedRodeoEvent.winners[winnerIndex].contestantOne = data.buddies[0];
                    this.selectedRodeoEvent.winners[winnerIndex].horseOne = null;
                }
            }
        });
    }

    openHorseSearch(event, winnerIndex: number, horses) {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        let popover = this.popoverController.create(HorseSearchPopover, {
            searchTitle: "Horse Search",
            horses: horses
        }, { cssClass: 'horse-search' });

        popover.present({ ev: event });

        popover.onDidDismiss((data) => {
            this.popoverActive = false;

            if (data != null) {
                this.selectedRodeoEvent.winners[winnerIndex].horseOne = (data.horse as Horse);
                this.selectedRodeoEvent.winners[winnerIndex].horseOneId = (data.horse as Horse).id;
            }
        });
    }

    openSecondSearch(event, rodeoEvent: RodeoEvent, winner: Winner, winnerIndex: number) {
        if (rodeoEvent.eventType == "TeamRoping") {
            this.openUserSearch(event, rodeoEvent, winnerIndex, true);
        } else {
            let loading = this.loadingControl.create({
                content: 'Retrieving user horses...',
            });

            loading.present();
            this.horseService.getHorses([winner.contestantOne.id])
                .then((data) => {
                    loading.dismiss();
                    this.openHorseSearch(event, winnerIndex, (data as Horse[]));
                }, error => {
                    loading.dismiss();
                    this.alert.showError("Horse Retrieval", "Unable to retrieve user horses.");
                });
        }
    }

    getFirstLabel(rodeoEvent: RodeoEvent) {
        return rodeoEvent.configuration.runs.slotOneLabel;
    }

    getSecondDisplay(rodeoEvent: RodeoEvent, winner: Winner) {
        var secondDisplayValue = '';

        var valuesToDisplay = rodeoEvent.configuration.runs.slotTwoFieldName.split('+');

        valuesToDisplay.forEach(value => {
            var propertiesInValue = value.split('.');

            var objectWithValue: any;
            objectWithValue = winner;

            var lastProperty = '';
            propertiesInValue.forEach(property => {
                if (!objectWithValue) {
                    objectWithValue = lastProperty == 'livestock' ? 'Livestock 1' : 'Horse 1';
                    return;
                }

                objectWithValue = objectWithValue[property];
                lastProperty = property;
            });

            secondDisplayValue += objectWithValue + ' ';
        });

        return secondDisplayValue.substring(0, secondDisplayValue.length - 1);
    }

    getSecondLabel(rodeoEvent: RodeoEvent) {
        return rodeoEvent.configuration.runs.slotTwoLabel;
    }

    getWinnerValue(winner: Winner) {
        if (this.selectedRodeoEvent.configuration.runs.scoreValue) {
            return (+winner.score).toFixed(this.selectedRodeoEvent.configuration.runs.decimalPlaces) + (winner.onXRounds > 1 ? ' on ' + winner.onXRounds : '');
        } else {
            return (+winner.time).toFixed(this.selectedRodeoEvent.configuration.runs.decimalPlaces) + (winner.onXRounds > 1 ? ' on ' + winner.onXRounds : '');
        }
    }

    isRoughStock() {
        if (this.selectedRodeoEvent.eventType === 'BullRiding' || this.selectedRodeoEvent.eventType === 'SaddleBroncRiding' || this.selectedRodeoEvent.eventType === 'RanchBroncRiding' || this.selectedRodeoEvent.eventType === 'BarebackRiding')
            return true;
        else
            return false;
    }

    filterWinners() {
        this.displayWinners = [];

        if (!this.selectedPayout)
            return;

        if (this.selectedPayout.name == 'All') {
            this.displayWinners = this.selectedRodeoEvent.winners;
        } else {
            this.displayWinners = this.selectedRodeoEvent.winners.filter(x => x.payoutConfigId == this.selectedPayout.id
                || (x.payoutConfigId == null));
        }
    }

    rebuildPayoutSelections() {
        this.payoutSelections = [];
        this.payoutSelections.push({ name: 'All'} as PayoutConfig);

        this.rodeo.payoutConfigs.forEach((payoutConfig) => {
            if (payoutConfig.sources.some(x => x.rodeoEventId == this.selectedRodeoEvent.id)) {
                this.payoutSelections.push(payoutConfig);
            }
        })

        if (!this.selectedPayout || !this.payoutSelections.some(x => x.name.toLocaleLowerCase() == this.selectedPayout.name.toLocaleLowerCase()))
            this.selectedPayout = this.payoutSelections[0];

        if (this.payoutSelections.indexOf(this.selectedPayout) == -1)
            this.selectedPayout = this.payoutSelections.find(x => x.name.toLocaleLowerCase() == this.selectedPayout.name.toLocaleLowerCase());

        this.totalCount = 0;

        if (this.selectedPayout.name != 'All') {
            this.selectedPayout.sources.forEach((source) => {
                let count = this.getSourceCount(this.selectedPayout, source);
                this.totalCount += count;
            });

            this.membershipDiscountTotal = LeaderGenerator.instance().getMembershipDiscountTotal(this.rodeo, this.selectedPayout, true);

            this.places = this.selectedPayout.places;
            this.addedMoney = this.selectedPayout.addedMoney;
        }

        this.filterWinners();
    }

    getRoundedPayout(payout) {
        if (this.rounding == "None")
            return Math.round(payout * 100 / 1) / 100;
        if (this.rounding == "1")
            return Math.round(payout / 1) * 1;
        else if (this.rounding == "5")
            return Math.round(payout / 5) * 5;
        else if (this.rounding == "5")
            return Math.round(payout / 5) * 5;
        else if (this.rounding == "10")
            return Math.round(payout / 10) * 10;
        else if (this.rounding == "20")
            return Math.round(payout / 20) * 20;
    }

    getDisplayTotal() {
        if (!this.displayWinners || this.displayWinners.length == 0)
            return;

        var displayTotal = this.displayWinners.map(x => x.payout).reduce((a, b) => {
            return a * 1 + b * 1;
        });

        return displayTotal
    }

    getTotalRodeoPayout() {
        if (!this.rodeo.rodeoEvents || this.rodeo.rodeoEvents.length == 0 || this.rodeo.rodeoEvents.flatMap(x => x.winners).length == 0)
            return;

        return this.rodeo.rodeoEvents.flatMap(x => x.winners).map(x => x.payout).reduce((a, b) => {
            return a * 1 + b * 1;
        });
    }

    matchRodeoEvents(a, b) {
        return a.id == b.id;
    }

    matchSchedules(a, b) {
        return a.id == b.id;
    }

    matchPayoutFormats(a, b) {
        return a.id == b.id;
    }

    matchSelections(a, b) {
        return a.name == b.name;
    }

    matchStrings(a, b) {
        return a == b;
    }

    updatedPayoutFormat() {
        if (this.selectedPayoutFormat)
            this.availablePlaces = [0, ...new Set(this.selectedPayoutFormat.placePercentages.map(x => x.totalPlaces).sort((a, b) => {
                if (+a > +b)
                    return 1;
                else if (+a < +b)
                    return -1;
                else
                    return 0;
            }))];
        else
            this.availablePlaces = [0];
    }

    getAvailablePlaces() {
        if (this.selectedPayoutFormat)
            return [0, ...new Set(this.selectedPayoutFormat.placePercentages.map(x => x.totalPlaces))];
        else
            return [0];
    }

    getTotalCount(payout) {
        var count = 0;

        payout.sources.forEach((source) => {
            count += this.getSourceCount(payout, source);
        })

        return count;
    }

    getSourceCount(payout: PayoutConfig, source) {
        return LeaderGenerator.instance().getTotalCount(this.rodeo, payout, source, true);
    }

    getTotalPot(payout: PayoutConfig, addMoney) {
        if (payout.enableManualPayouts) {
            return payout.manualPayoutsArray.reduce((a, b) => +a + +b);
        } else {
            this.projectedPot = addMoney ? +this.addedMoney : 0;

            if (!payout || !payout.sources)
                return;

            payout.sources.forEach((source) => {
                this.projectedPot += +(this.getSourceCount(payout, source) * source.amountToPot);
            })

            return this.projectedPot -= this.membershipDiscountTotal;
        }
    }

    getFee(payout: PayoutConfig) {
        return payout.sources.length != 0 ? payout.sources[0].amountToPot : 0;
    }

    async updatePayouts() {
        //this.rebuildPayoutSelections();

        let changePayouts = () => {
            let payoutsToProcess: PayoutConfig[] = [];

            if (this.selectedPayout.name == 'All') {
                payoutsToProcess = this.payoutSelections.filter(x => x.name != 'All');
                this.selectedRodeoEvent.winners = [];
            } else {
                payoutsToProcess.push(this.selectedPayout);
            }

            payoutsToProcess.forEach((payout) => {
                var winnersToDelete: Winner[] = [];

                winnersToDelete = this.selectedRodeoEvent.winners.filter(x => x.payoutConfigId == this.selectedPayout.id
                    || (x.payoutConfigId == null && this.selectedPayout.sources.length == 0
                        && x.round == this.selectedPayout.sources[0].round
                        && x.rotation == this.selectedPayout.sources[0].rotation
                        && x.isAggregate == this.selectedPayout.sources[0].isAggregate
                        && x.isAverage == this.selectedPayout.sources[0].isAverage
                        && ((x.scheduleId == null && this.selectedPayout.sources[0].scheduleId == null) || (this.selectedPayout.sources[0].scheduleId != null && this.selectedPayout.sources[0].scheduleId == x.scheduleId))
                        && ((x.sidepotId == null && this.selectedPayout.sources[0].sidepotId == null) || (this.selectedPayout.sources[0].sidepotId != null && this.selectedPayout.sources[0].sidepotId == x.sidepotId))
                    ));

                this.selectedRodeoEvent.winners = this.selectedRodeoEvent.winners.filter(x => !winnersToDelete.find(y => y == x));

                this.deleteWinners(winnersToDelete);

                this.totalPot = this.getTotalPot(payout, false);

                this.leaders = LeaderGenerator.instance().getLeaders(this.rodeo, payout, this.places, payout.includePenalties, this.selectedRodeoEvent.fallBackToFill, this.selectedRodeoEvent.fallBackToPlace);
                
                let rodeoEventPot = +this.totalPot + (this.addedDistribution == 'Progressive' ? +this.addedMoney : 0);
                let payoutPlaces = this.selectedPayoutFormat.placePercentages.filter(x => x.totalPlaces == this.places);

                if (!this.testPay) {
                    var removedLeaderGroups = this.leaders.filter(x => x.runs.length == 0);

                    if (removedLeaderGroups.length > 0) {
                        var single = removedLeaderGroups.length;
                        this.alert.showConfirmation("Winner Groups Excluded", new String(removedLeaderGroups.map(x => x.name)) + (single ? " was" : " were") + " removed because no one placed in " + (single ? "it." : "them."));
                    }

                    this.leaders = this.leaders.filter(x => x.runs.length > 0);
                }

                for (let i = 0; i < this.leaders.length; i++) {
                    let winnerSetPot = rodeoEventPot;
                    if (this.equalDPots) {
                        winnerSetPot *= 1 / this.leaders.length;
                    } else if (this.leaders.length > 0 && this.selectedPayoutFormat.placePercentages.some(x => x.ds == true))
                        winnerSetPot *= this.selectedPayoutFormat.placePercentages.filter(x => x.ds == true && x.totalPlaces == this.leaders.length).filter(x => x.place == (i + 1))[0].payoutPercentage / 100;
                    else
                        winnerSetPot *= this.selectedPayoutFormat.placePercentages.filter(x => x.ds == false && x.totalPlaces == this.leaders.length).filter(x => x.place == (i + 1))[0].payoutPercentage / 100;

                    if (this.addedDistribution == 'Equal' && this.addedMoney > 0)
                        winnerSetPot += (+this.addedMoney / this.leaders.length);

                    var currentLeaderGroupPlaces = Math.min(this.leaders[i].runs.length, this.places);

                    if (this.testPay)
                        currentLeaderGroupPlaces = Math.max(this.leaders[i].runs.length, this.places);

                    var winnersAdded = [];
                    if (this.leaders[i]) {
                        for (let h = 0; h < (this.testPay ? this.places : this.leaders[i].runs.length); h++) {
                            let newWinner = new Winner();
                            newWinner.edited = true;
                            let secondWinner = new Winner();
                            secondWinner.edited = true;
                            payoutPlaces = this.selectedPayoutFormat.placePercentages.filter(x => x.ds == false && x.totalPlaces == currentLeaderGroupPlaces);

                            if (!this.testPay && h < this.leaders[i].runs.length) {
                                newWinner.contestantOneId = this.leaders[i].runs[h].contestantOneId;
                                newWinner.contestantOne = this.leaders[i].runs[h].contestantOne;
                                newWinner.contestantTwoId = this.leaders[i].runs[h].contestantTwoId;
                                newWinner.contestantTwo = this.leaders[i].runs[h].contestantTwo;
                                newWinner.isExtraRunForContestantOne = this.leaders[i].runs[h].isExtraRunForContestantOne;
                                newWinner.isExtraRunForContestantTwo = this.leaders[i].runs[h].isExtraRunForContestantTwo;

                                newWinner.rodeoEventId = this.selectedRodeoEvent.id;
                                newWinner.name = this.leaders[i].name;
                                newWinner.horseOneId = this.leaders[i].runs[h].horseOneId;
                                newWinner.horseOne = this.leaders[i].runs[h].horseOne;
                                newWinner.horseTwoId = this.leaders[i].runs[h].horseTwoId;
                                newWinner.horseTwo = this.leaders[i].runs[h].horseTwo;

                                newWinner.livestockId = this.leaders[i].runs[h].livestockId;
                                newWinner.livestock = this.leaders[i].runs[h].livestock;

                                newWinner.number = this.leaders[i].runs[h].number + this.leaders[i].runs[h].extraLabel;
                                newWinner.place = h + 1;

                                if (payout.enableManualPayouts) {
                                    newWinner.payout = h >= payout.manualPayoutsArray.length ? 0 : +payout.manualPayoutsArray[h];
                                } else {
                                    newWinner.payout = h >= currentLeaderGroupPlaces ? 0 : this.getRoundedPayout(payoutPlaces.filter(x => x.place == newWinner.place)[0].payoutPercentage * winnerSetPot / 100);
                                }

                                newWinner.isAggregate = payout.isAggregate;
                                newWinner.isAverage = payout.isAverage;
                                newWinner.round = this.leaders[i].runs[h].round;
                                newWinner.onXRounds = payout.isAggregate || payout.isAverage ? this.leaders[i].runs[h].displaySuccessfulRuns : 1;
                                newWinner.rotation = this.leaders[i].runs[h].rotation;
                                newWinner.payoutConfigId = payout.id == 0 ? null : payout.id;

                                if (payout.name.includes("Barrel One Split") || payout.name.includes("Home Split")) {
                                    if (payout.name.includes('Barrel One Split')) {
                                        newWinner.time = +this.leaders[i].runs[h].straightOneSplit + +this.leaders[i].runs[h].aroundBarrelOneSplit;
                                    } else if (payout.name.includes('Home Split')) {
                                        newWinner.time = +this.leaders[i].runs[h].straightFourSplit;
                                    }
                                } else {
                                    newWinner.time = (payout.isAverage || payout.isAggregate) ? this.leaders[i].runs[h].totalTime : this.leaders[i].runs[h].time;
                                    newWinner.score = (payout.isAverage || payout.isAggregate) ? this.leaders[i].runs[h].totalScore : this.leaders[i].runs[h].score;    
                                }
                                
                                newWinner.place = payout.name.includes("Bottom's Up") ? h + this.leaders[i].startPlace : h + this.leaders[i].startPlace;

                                winnersAdded.push(newWinner);
                                this.selectedRodeoEvent.winners.push(newWinner);
                                this.displayWinners.push(newWinner);

                                var maxIndex = this.displayWinners.length - 1;
                                var potToSplit = newWinner.payout;
                                var index = 0;
                                while (maxIndex + index > 0) {
                                    if (this.selectedRodeoEvent.configuration.runs.scoreValue) {
                                        if (this.displayWinners[(maxIndex + index)].score == this.displayWinners[(maxIndex - 1 + index)].score) {
                                            potToSplit += this.displayWinners[(maxIndex - 1 + index)].payout;
                                            index--;
                                        }
                                        else
                                            break;
                                    } else {
                                        if (this.displayWinners[(maxIndex + index)].time == this.displayWinners[(maxIndex - 1 + index)].time) {
                                            potToSplit += this.displayWinners[(maxIndex - 1 + index)].payout;
                                            index--;
                                        } 
                                        else
                                            break;
                                    }
                                }

                                if (index != 0) {
                                    var individualPot = potToSplit / (Math.abs(index) + 1)
                                    while (index <= 0) {
                                        this.displayWinners[maxIndex + index].payout = +individualPot.toFixed(1);
                                        index++;
                                    }

                                    newWinner.payout = +individualPot.toFixed(1);
                                }
                            } else {
                                if (payout.enableManualPayouts) {
                                    this.addExampleWinner(payout, this.leaders[i].name, h + 1, h >= payout.manualPayoutsArray.length ? 0 : +payout.manualPayoutsArray[h])
                                } else {
                                    this.addExampleWinner(payout, this.leaders[i].name, h + 1, this.getRoundedPayout(payoutPlaces.filter(x => x.place == h + 1)[0].payoutPercentage * winnerSetPot / 100))
                                }
                            }
                        }

                        if (!this.testPay && payout.enableManualPayouts && winnersAdded.length < payout.manualPayoutsArray.length) {
                            var groundMoneyTotal = 0;

                            for (let h = winnersAdded.length; h < payout.manualPayoutsArray.length; h++) {
                                groundMoneyTotal += +payout.manualPayoutsArray[h];
                            }

                            winnersAdded.forEach((winner) => {
                                winner.payout += this.getRoundedPayout(groundMoneyTotal / winnersAdded.length);
                            });
                        }
                    } else {
                        for (let h = 0; h < this.places; h++) {
                            this.addExampleWinner(payout, this.leaders[i].name, h + 1, this.getRoundedPayout(payoutPlaces.filter(x => x.place == h)[0].payoutPercentage * winnerSetPot / 100));
                        }
                    }
                }
            });

            this.events.publish("WinnersChanged");

            this.sortWinners();
            this.filterWinners();
        };

        this.popoverActive = true;
        let alert = this.alertController.create({
            title: 'Confirm Payout Update',
            message: 'Do you want to modify the payouts?',
            buttons: [
                {
                    text: 'No',
                    role: 'cancel',
                    handler: () => {
                    }
                },
                {
                    text: 'Yes',
                    handler: changePayouts
                }]
        });

        alert.present();

        alert.onDidDismiss(() => {
            this.popoverActive = false;
        })
    }

    addExampleWinner(payoutConfig, name, place, payout) {
        let newWinner = new Winner();
        newWinner.payoutConfigId = payoutConfig.id == 0 ? null : payoutConfig.id;
        newWinner.contestantOneId = 1;
        newWinner.contestantOne = new Account();
        newWinner.contestantOne.id = 1;
        newWinner.contestantOne.firstName = "Example";
        newWinner.contestantOne.lastName = "Winner";
        newWinner.contestantOne.birthDate = "1999-01-01";
        newWinner.horseOne = new Horse();
        newWinner.horseOne.name = "Horse 1";

        if (this.selectedRodeoEvent.eventType == 'TeamRoping') {
            newWinner.contestantTwoId = 1;
            newWinner.contestantTwo = new Account();
            newWinner.contestantTwo.id = 1;
            newWinner.contestantTwo.firstName = "Example";
            newWinner.contestantTwo.lastName = "Winner";
            newWinner.contestantTwo.birthDate = "1999-01-01";
            newWinner.horseTwo = new Horse();
            newWinner.horseTwo.name = "Horse 2";
        }

        newWinner.rodeoEventId = this.selectedRodeoEvent.id;
        newWinner.name = name;
        newWinner.livestock = new Livestock();
        newWinner.livestock.brand = place + 1 + "";
        newWinner.livestock.name = "";

        newWinner.number = place;
        newWinner.place = place;
        newWinner.payout = payout;
        newWinner.time = 0;
        newWinner.score = 0;

        this.selectedRodeoEvent.winners.push(newWinner);
        this.displayWinners.push(newWinner);
    }

    setWinnerName(newWinner, payout, averageMode, aggregate, i, leaderSchedule) {
        if (payout.sidepot)
            newWinner.name = payout.name + (this.leaders.length > 1 ? " " + (i + 1) + "D" : "");
        else {
            if (averageMode)
                newWinner.name = 'Average' + (this.selectedRodeoEvent.leaderDivisions ? (this.leaders.length > 1 ? " " + (i + 1) + "D" : "") : '');
            else if (aggregate)
                newWinner.name = 'Aggregate' + (this.selectedRodeoEvent.leaderDivisions ? (this.leaders.length > 1 ? " " + (i + 1) + "D" : "") : '');
            else {
                if (this.selectedRodeoEvent.leaderDivisions) {
                    newWinner.name = (i + 1) + "D" + (payout.name.includes("Bottom's Up") ? " " + payout.name : "") + (payout.round != 0 ? ' - ' : '');
                } else {
                    newWinner.name = "";
                }

                if (payout.round != 0)
                    newWinner.name += (payout.round > this.selectedRodeoEvent.rounds ? "Short-Go" : "Round " + payout.round);
            }

            if (leaderSchedule)
                newWinner.name = leaderSchedule.name + ' - ' + newWinner.name;
        }
    }

    shouldShowCategoryTotal(winnerIndex) {
        return winnerIndex >= this.displayWinners.length - 1 || this.displayWinners[winnerIndex + 1].name != this.displayWinners[winnerIndex].name;
    }

    getCategoryTotal(winnerIndex) {
        let name = this.displayWinners[winnerIndex].name;

        return this.displayWinners.filter(x => x.name == name).map(x => x.payout).reduce((a, b) => {
            return a * 1 + b * 1;
        });
    }

    getCategoryPercent(winnerIndex) {
        let categoryTotal = this.getCategoryTotal(winnerIndex);
        
        if (categoryTotal == 0)
            return 0;

        return (categoryTotal / (this.displayWinners.filter(x => x.payoutConfigId == this.displayWinners[winnerIndex].payoutConfigId).map(x => x.payout).reduce((a, b) => {
            return a * 1 + b * 1;
        })) * 100).toFixed(2);
    }

    sortWinners() {
        this.selectedRodeoEvent.winners.sort((a, b) => {
            let aIndex = this.payoutSelections.map(x => x.name).indexOf(a.name);
            let bIndex = this.payoutSelections.map(x => x.name).indexOf(b.name);

            if (aIndex > bIndex)
                return 1;
            else if (aIndex < bIndex)
                return -1;

            if (a.name > b.name)
                return 1;
            else if (a.name < b.name)
                return -1;

            if (+a.place > +b.place)
                return 1;
            else if (+a.place < +b.place)
                return -1;

            if (+a.time > +b.time)
                return 1;
            else if (+a.time < +b.time)
                return -1;
            else
                return 0;
        });
    }

    printResults() {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        this.nav.push(ResultsReport, {
            rodeo: this.rodeo,
            printing: true,
            selectedRodeoEvent: this.selectedRodeoEvent,
            selectedMode: this.selectedPayout,
            winners: this.testPay ? this.displayWinners : null,
            winnersOnly: false
        }).then(() => { this.popoverActive = false; });
    }

    printWinners() {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        this.nav.push(ResultsReport, {
            rodeo: this.rodeo,
            selectedRodeoEvent: this.selectedRodeoEvent,
            printing: true,
            winners: this.displayWinners,
            winnersOnly: true
        }).then(() => { this.popoverActive = false; });
    }

    updatedPayoutSelection() {
        if (this.selectedPayout.name != 'All') {
            this.totalCount = this.getTotalCount(this.selectedPayout);
            this.membershipDiscountTotal = LeaderGenerator.instance().getMembershipDiscountTotal(this.rodeo, this.selectedPayout, true);
    
            this.selectedPayoutFormat = this._rodeo.organization.payoutFormats.filter(x => x.payoutFormat.id == this.selectedPayout.payoutFormatId)[0].payoutFormat;
            this.places = this.selectedPayout.places;
            this.addedMoney = this.selectedPayout.addedMoney;
    
            this.getTotalPot(this.selectedPayout, true);
        }

        this.filterWinners();
    }

    addWinner() {
        if (this.popoverActive)
            return;

        this.popoverActive = true;
        let entryModal = this.modalCtrl.create(NewWinnerPage, { rodeoEvent: this.selectedRodeoEvent }, { cssClass: 'page-new-winner', enableBackdropDismiss: false });

        entryModal.present();
        entryModal.onDidDismiss((data) => {
            this.popoverActive = false;

            if (data != null && data != undefined) {
                if (!data.winner.livestock) {
                    data.winner.livestock = new Livestock();
                    data.winner.livestock.name = '';
                }

                this.selectedRodeoEvent.winners.push(data.winner);
                this.sortWinners();
            }
        });
    }

    async addWinners(winners: Winner[]) {
        if (winners.length == 0)
        {
            this.alert.showError("No Winners", "Nothing to save.");
            return;
        }

        winners.forEach(winner => {
            if (winner.contestantOne)
                winner.contestantOneId = winner.contestantOne.id;

            if (winner.horseOne)
                winner.horseOneId = winner.horseOne.id;

            if (winner.contestantTwo)
                winner.contestantTwoId = winner.contestantTwo.id;

            if (winner.horseTwo)
                winner.horseTwoId = winner.horseTwo.id;

            if (winner.livestock)
                winner.livestockId = winner.livestock.id;
        })

        let loading = this.loadingControl.create({
            content: 'Saving winners...',
        });

        winners = winners.filter(x => x.contestantOneId != 1);

        if (winners.length == 0)
        {
            this.alert.showError("Test Pay", "Nothing to save.");
            return;
        }

        const winnersPayoutIds = Array.from(new Set(winners.map(w => w.payoutConfigId)));
        const winnerPayouts = this.rodeo.payoutConfigs.filter(p => winnersPayoutIds.includes(p.id));   

        loading.present();
       
        this.winnerService.addWinners(winners)
            .then(data => {
                loading.dismiss();
                if ((data as any).message != null && (data as any).message != undefined) {
                    this.alert.showError("Winner Failed", (data as any).message);
                } else {
                    this.selectedRodeoEvent.winners = this.selectedRodeoEvent.winners.filter(x => !winners.find(y => y == x));
                    this.selectedRodeoEvent.winners = this.selectedRodeoEvent.winners.concat(data as Winner[]);

                    this.sortWinners();
                    this.filterWinners();
                }
                try {
                    const pointStatsGenerationStatus = this.pointsDomainService.verifyPointStatsConfiguration(this.rodeo, this.selectedRodeoEvent, winnerPayouts);
                    if (!pointStatsGenerationStatus.error) {
                        const pointStatusModal = this.modalCtrl.create(PointGenerationModalComponent, {
                            pointStatsGenerationStatus: pointStatsGenerationStatus,
                            rodeoEvent: this.selectedRodeoEvent,
                            rodeo: this.rodeo,
                            winnerPayouts: winnerPayouts,
                            useScoreValue: this.selectedRodeoEvent.configuration.runs.scoreValue
                        }, { enableBackdropDismiss: false });
                        pointStatusModal.present();
                        pointStatusModal.onDidDismiss((_data, role) => {
                            if(role === 'confirm') {
                                this.alert.showError('Points generation', 'Points saved');
                            }
                        })
                    }
                } catch(ex) {
                    this.monitoringService.logException(ex);            
                    console.log(ex);
                }
            }, error => {
                loading.dismiss();
                this.alert.showError("Winner Create Error", error);
            });
    }

    async deleteWinners(winners: Winner[]) {
        var winnerIdsToDelete = [];

        winners.forEach((winner) => {
            if (!winner.id) {
                this.displayWinners = this.displayWinners.filter(x => x != winner);
            } else {
                winnerIdsToDelete.push(winner.id);
            }
        })

        if (winnerIdsToDelete.length == 0) {
            return;
        }

        try {
            await this.winnerService.deleteWinners(winnerIdsToDelete);
            this.selectedRodeoEvent.winners = this.selectedRodeoEvent.winners.filter(x => !winners.find(y => y == x));

            this.sortWinners();
            this.filterWinners();
        } catch (error) {
            this.alert.showError("Winner Removal Error", error);
        } finally {
            this.popoverActive = false;
        }
    }

    deleteWinner(winner: Winner) {
        if (!winner.id) {
            this.displayWinners = this.displayWinners.filter(x => x != winner);
            this.selectedRodeoEvent.winners = this.selectedRodeoEvent.winners.filter(x => x != winner);
            return;
        }

        if (this.popoverActive)
            return;

        this.popoverActive = true;

        let alert = this.alertController.create({
            title: 'Confirm Winner Removal',
            message: 'Are you sure you want to remove #' + winner.place + ' ' + winner.contestantOne.firstName + ' ' + winner.contestantOne.lastName + '?',
            buttons: [
                {
                    text: 'Yes',
                    handler: () => {
                        this.winnerService.deleteWinner(winner.id)
                            .then(data => {
                                this.selectedRodeoEvent.winners = this.selectedRodeoEvent.winners.filter(x => x.id != winner.id);

                                this.sortWinners();
                                this.filterWinners();
                                this.popoverActive = false;
                            }, error => {
                                this.alert.showError("Winner Removal Error", error);
                                this.popoverActive = false;
                            });
                    }
                },
                {
                    text: 'No',
                    role: 'cancel',
                    handler: () => { }
                }]
        });

        alert.present();

        alert.onDidDismiss(() => {
            this.popoverActive = false;
        })
    }

    printChecks(winners: Winner[]) {
        var checksToPrint: OrganizationCheckRegister[] = [];

        for (var i = 0; i < winners.length; i++) {
            var payoutConfig = this.payoutSelections.filter(x => x.id == winners[i].payoutConfigId)[0];

            if (payoutConfig == null)
                continue;

            if (winners[i].contestantTwo != null && payoutConfig.contestantTwoPayout) {
                let contestantTwoCheck = this.getCheckFromWinner(winners[i], true, payoutConfig);

                checksToPrint.push(contestantTwoCheck);
            }

            if (payoutConfig.contestantOnePayout) {
                let contestantOneCheck = this.getCheckFromWinner(winners[i], false, payoutConfig);

                checksToPrint.push(contestantOneCheck);
            }
        }

        this.modalCtrl.create(
            PrintChecksComponent,
            { checksToPrint, rodeo: this._rodeo, rodeoEvent: this.selectedRodeoEvent },
            { cssClass: 'print-checks-modal' }
        ).present();
    }

    getCheckFromWinner(winner: Winner, teammate: boolean, payoutConfig: PayoutConfig): OrganizationCheckRegister {
        let payoutAmount = winner.payout;

        if (payoutConfig.contestantOnePayout && payoutConfig.contestantTwoPayout && winner.contestantTwo != null)
            payoutAmount = (winner.payout / 2);

        let checkRegister = new OrganizationCheckRegister();
        checkRegister.organizationId = this.rodeo.organization.id;
        checkRegister.rodeoId = this.rodeo.id;
        checkRegister.name = teammate ? (winner.contestantTwo ? winner.contestantTwo.firstName + ' ' + winner.contestantTwo.lastName : '') : winner.contestantOne.firstName + ' ' + winner.contestantOne.lastName;
        checkRegister.memo = `${winner.name} - ${winner.place}: $${payoutAmount}`;
        checkRegister.streetAddress = teammate ? (winner.contestantTwo ? winner.contestantTwo.streetAddress : '') : winner.contestantOne.streetAddress;
        checkRegister.city = teammate ? (winner.contestantTwo ? winner.contestantTwo.city : '') : winner.contestantOne.city;
        checkRegister.state = teammate ? (winner.contestantTwo ? winner.contestantTwo.state : '') : winner.contestantOne.state;
        checkRegister.zipcode = teammate ? (winner.contestantTwo ? winner.contestantTwo.zip : '') : winner.contestantOne.zip;
        checkRegister.amount = payoutAmount + '';
        checkRegister.time = winner.time;
        checkRegister.score = winner.score;

        return checkRegister;
    }
}


// WEBPACK FOOTER //
// ./src/pages/staff/rodeo/results/results.ts